import { PRIVILEGES } from 'constants/privileges';
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  getItemDisabledErrorMessage,
  KNOWN_RESPONSE_MESSAGES,
  KNOWN_RESPONSE_STATUSES,
} from 'api/const';
import { Storehouse } from 'api/counterparties/counterparties.types';
import { useCreateTransportCloudEntry } from 'api/transportCloud';
import { StyledButton } from 'components/Buttons';
import { StyledTextField } from 'components/Inputs';
import { useAuth } from 'contexts/authContext';
import _, { debounce } from 'lodash';
import CurrencyAutocomplete from 'pages/invoices/components/InvoiceForm/elements/CurrencyAutocomplete';
import ContainerTypeAutocomplete from 'pages/rates/components/RatesForm/elements/ContainerTypeAutocomplete';
import ShipownerAutocomplete from 'pages/rates/components/RatesForm/elements/ShipownerAutocomplete';
import TradeTypeAutocomplete from 'pages/rates/components/RatesForm/elements/TradeTypeAutocomplete';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { generatePath, Navigate, useNavigate } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import { hasPrivilege } from 'utils/authFunctions.utils';
import { appToast } from 'utils/toast.utils';
import { ContainerNumbersInput } from './elements/ContainerNumbersInput';
import CustomerAutocomplete from './elements/CustomerAutocomplete';
import LoadingDeliveryLocationAutocomplete from './elements/LoadingDeliveryLocationAutocomplete';
import TruckerAutocomplete from './elements/TruckerAutocomplete';
import {
  getCustomerId,
  getIds,
  getTruckerId,
  isSuccessful,
} from './TransportCloud.utils';
import {
  VALIDATION,
  GENERAL,
  BUTTONS,
  LABELS,
  MESSAGES,
} from './TransportCloudForm.consts';
import * as S from './TransportCloudForm.css';
import {
  getTruckersRateValidationRules,
  getCurrencyCodeValidationRules,
  getContainerNumbersValidationRules,
} from './transportCloudValidators';
import { currencyCode, FormInputs, TransportCloudFormPayload } from './types';

const debouncedTrigger = debounce((fn) => fn(), 100);

export const TransportCloudForm = () => {
  const { user } = useAuth();

  if (!hasPrivilege(PRIVILEGES.TransportCloud.ADD_SHIPMENT_PLANS, user)) {
    return <Navigate to={reactPaths.home} replace />;
  }

  return <TransportCloudFormInner />;
};

const TransportCloudFormInner = () => {
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      date: '',
      currencyCode: null,
      containerNumbers: [],
      comment: '',
    },
  });

  const [submitButtonDisabled, setSubmitButtonDisabled] =
    useState<boolean>(false);

  const [currencyOptions, setCurrencyOptions] = useState<currencyCode[]>([]);
  const [currencyParams, setCurrencyParams] = useState({
    filterText: '',
    pageNumber: 1,
  });

  const navigate = useNavigate();
  const { createTransportCloudEntry } = useCreateTransportCloudEntry();

  const dateValue = watch('date');
  const truckersRateValue = watch('truckersRate');
  const currencyCodeValue = watch('currencyCode');
  const amountValue = watch('amount');
  const containerNumbersValue = watch('containerNumbers');
  const customerValue = watch('customer');

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (data) {
      const transportDate = new Date(data.date).toISOString();

      const payload: TransportCloudFormPayload = {
        transportDate,
        tradeType: data.tradeType?.name || null,
        customerId: data.customer ? getCustomerId(data.customer) : null,
        shippingLineId: data.shipowner ? getIds(data.shipowner) : null,
        truckerId: data.trucker ? getTruckerId(data.trucker) : null,
        comment: data.comment || null,
        description: null,
        containerRequests: [
          {
            containerId: data.containerType ? getIds(data.containerType) : 0,
            quantity: Number(data.amount) || 1,
            containerCodes: data.containerNumbers || [],
          },
        ],
      };

      if (data.truckersRate && data.currencyCode) {
        payload.truckerRate = {
          value: Number(data.truckersRate),
          currencyCode: {
            id: getIds(data.currencyCode),
          },
        };
      }

      if (
        data.loadingDeliveryLocation &&
        typeof data.loadingDeliveryLocation === 'object' &&
        data.loadingDeliveryLocation !== null
      ) {
        const locationData = data.loadingDeliveryLocation as Storehouse;
        payload.location = {
          lineOne: locationData.address.lineOne || undefined,
          lineTwo: locationData.address.lineTwo || undefined,
          city: locationData.address.city || undefined,
          countryCode: locationData.address.countryCode || undefined,
        };
      }

      setSubmitButtonDisabled(true);
      try {
        const res = await createTransportCloudEntry(payload);
        if (res.status && isSuccessful(res)) {
          appToast.success(MESSAGES.createSuccess);
          navigate(generatePath(reactPaths.transportCloud));
        } else if (
          res.data.status === KNOWN_RESPONSE_STATUSES.BAD_REQUEST &&
          res.data.message === KNOWN_RESPONSE_MESSAGES.ITEM_DISABLED
        ) {
          const { type, code } = res.data.result;
          const errMsg = getItemDisabledErrorMessage({ type, code });
          appToast.error(errMsg);
        } else {
          appToast.error(MESSAGES.createError);
        }
      } catch (error) {
        appToast.error(MESSAGES.createError);
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  };

  const cancelHandler = () => {
    navigate(generatePath(reactPaths.transportCloud));
  };

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    debouncedTrigger(() => {
      trigger(['truckersRate', 'currencyCode']);
    });
  }, [truckersRateValue, currencyCodeValue, trigger]);

  useEffect(() => {
    debouncedTrigger(() => {
      trigger();
    });
  }, [dateValue, trigger]);

  useEffect(() => {
    debouncedTrigger(() => {
      trigger('containerNumbers');
    });
  }, [amountValue, containerNumbersValue, trigger]);

  useEffect(() => {
    if (truckersRateValue && !currencyCodeValue && currencyOptions.length > 0) {
      const plnCurrency = currencyOptions.find(
        (currency) => currency.currency === 'PLN',
      );
      if (plnCurrency) {
        setValue('currencyCode', plnCurrency);
      }
    }
  }, [truckersRateValue, currencyCodeValue, currencyOptions, setValue]);

  return (
    <Box display='flex' flexDirection='column'>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <S.Title>{GENERAL.title}</S.Title>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='date'>{LABELS.date}</S.FieldLabel>
          <StyledTextField
            id='date'
            type='date'
            size='small'
            variant='outlined'
            padding='dense'
            width={280}
            error={!!errors.date}
            {...register('date', { required: true })}
          />
          <S.ErrorWrapper>
            {errors.date && <S.Error>{VALIDATION.fieldRequired}</S.Error>}
          </S.ErrorWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='containerType'>
            {LABELS.containerSize}
          </S.FieldLabel>
          <Controller
            name='containerType'
            control={control}
            rules={{ required: true }}
            render={(props) => (
              <ContainerTypeAutocomplete
                formProps={props}
                error={!!errors.containerType}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.containerType && (
              <S.Error>{VALIDATION.fieldRequired}</S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='amount'>{LABELS.amount}</S.FieldLabel>
          <StyledTextField
            id='amount'
            padding='dense'
            size='small'
            variant='outlined'
            type='number'
            width={280}
            error={!!errors.amount}
            {...register('amount', {
              required: true,
              min: { value: 1, message: VALIDATION.amount },
            })}
          />
          <S.ErrorWrapper>
            {errors.amount?.type === 'required' && (
              <S.Error>{VALIDATION.fieldRequired}</S.Error>
            )}
            {errors.amount?.type === 'min' && (
              <S.Error>{errors.amount.message}</S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{LABELS.shipowner}</S.FieldLabel>
          <Controller
            name='shipowner'
            control={control}
            rules={{ required: true }}
            render={(props) => (
              <ShipownerAutocomplete
                formProps={props}
                error={!!errors.shipowner}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.shipowner && <S.Error>{VALIDATION.fieldRequired}</S.Error>}
          </S.ErrorWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{LABELS.customer}</S.FieldLabel>
          <Controller
            name='customer'
            control={control}
            rules={{ required: true }}
            render={(props) => (
              <CustomerAutocomplete
                formProps={props}
                error={!!errors.customer}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.customer && <S.Error>{VALIDATION.fieldRequired}</S.Error>}
          </S.ErrorWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{LABELS.loadingDeliveryLocation}</S.FieldLabel>
          <Controller
            name='loadingDeliveryLocation'
            control={control}
            render={(props) => (
              <LoadingDeliveryLocationAutocomplete
                formProps={props}
                error={!!errors.loadingDeliveryLocation}
                customerId={customerValue ? getCustomerId(customerValue) : null}
              />
            )}
          />
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{LABELS.relation}</S.FieldLabel>
          <Controller
            name='tradeType'
            control={control}
            render={(props) => (
              <TradeTypeAutocomplete
                formProps={props}
                error={!!errors.tradeType}
              />
            )}
          />
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='truckersRate'>
            {LABELS.truckersRate}
          </S.FieldLabel>
          <StyledTextField
            id='truckersRate'
            padding='dense'
            size='small'
            variant='outlined'
            type='number'
            width={280}
            error={!!errors.truckersRate}
            sx={{
              '& .MuiInputBase-input': {
                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '&[type=number]': {
                  MozAppearance: 'textfield',
                },
              },
            }}
            {...register('truckersRate', {
              ...getTruckersRateValidationRules(getValues),
              onChange: () => {
                setTimeout(() => trigger('currencyCode'), 0);
              },
            })}
          />
          <S.ErrorWrapper>
            {errors.truckersRate && (
              <S.Error>{errors?.truckersRate?.message}</S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{LABELS.currency}</S.FieldLabel>
          <Controller
            name='currencyCode'
            control={control}
            rules={getCurrencyCodeValidationRules(getValues)}
            render={(props) => (
              <CurrencyAutocomplete
                formProps={{
                  ...props,
                  field: {
                    ...props.field,
                    onChange: (value) => {
                      props.field.onChange(value);
                      setTimeout(() => trigger('truckersRate'), 0);
                    },
                  },
                }}
                options={currencyOptions}
                setOptions={setCurrencyOptions}
                params={currencyParams}
                setParams={setCurrencyParams}
                handleCurrencyEdit={() => {}}
                handleOwnCurrencyEdit={() => {}}
                invoiceClassValue={null}
                isEditing={false}
                error={!!errors.currencyCode}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.currencyCode && 'message' in errors.currencyCode && (
              <S.Error>
                {(errors.currencyCode as { message?: string }).message}
              </S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='containerNumbers'>
            {LABELS.containerNumbers}
          </S.FieldLabel>
          <Controller
            name='containerNumbers'
            control={control}
            rules={getContainerNumbersValidationRules(getValues)}
            render={({ field, fieldState }) => (
              <ContainerNumbersInput
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!errors.containerNumbers}
                errorMessage={fieldState.error?.message}
                maxCount={amountValue && amountValue > 0 ? amountValue : 0}
                width={280}
              />
            )}
          />
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{LABELS.trucker}</S.FieldLabel>
          <Controller
            name='trucker'
            control={control}
            render={(props) => (
              <TruckerAutocomplete formProps={props} error={!!errors.trucker} />
            )}
          />
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='comment'>{LABELS.comment}</S.FieldLabel>
          <StyledTextField
            id='comment'
            padding='dense'
            size='small'
            variant='outlined'
            width={280}
            {...register('comment')}
          />
          <S.ErrorWrapper>
            <S.Error>{errors?.comment?.message}</S.Error>
          </S.ErrorWrapper>
        </S.FieldGroup>
        <S.ActionsContainer>
          <StyledButton
            $primary
            type='submit'
            disabled={(!_.isEmpty(errors) || submitButtonDisabled) as boolean}
          >
            {BUTTONS.addEntry}
          </StyledButton>
          <StyledButton type='button' onClick={cancelHandler}>
            {BUTTONS.cancel}
          </StyledButton>
        </S.ActionsContainer>
      </S.Form>
    </Box>
  );
};
